/**
 * Language ↔ URL helpers for bilingual routing.
 *
 * Strategy: English is the default and lives at the bare path (`/technology`).
 * Korean lives under a `/ko` path prefix (`/ko/technology`). The home pages
 * are `/` (en) and `/ko` (ko). This gives each language a distinct, indexable
 * URL so we can declare canonical + hreflang alternates.
 *
 * This module is DOM-free so it can be imported from the SEO generator (Node).
 */

export type Lang = 'en' | 'ko'

export const SUPPORTED_LANGS: Lang[] = ['en', 'ko']
export const DEFAULT_LANG: Lang = 'en'
export const KO_PREFIX = '/ko'

export interface ParsedPath {
  lang: Lang
  /** Language-independent route path, always starting with '/'. */
  logicalPath: string
}

/** Split a real URL pathname into { lang, logicalPath }. */
export function parsePath(pathname: string): ParsedPath {
  // Normalise: strip trailing slash (except root)
  let p = pathname || '/'
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1)

  if (p === KO_PREFIX) {
    return { lang: 'ko', logicalPath: '/' }
  }
  if (p.startsWith(KO_PREFIX + '/')) {
    return { lang: 'ko', logicalPath: p.slice(KO_PREFIX.length) }
  }
  return { lang: 'en', logicalPath: p }
}

/** Strip any leading /ko prefix from a path, returning the logical path. */
export function toLogicalPath(pathname: string): string {
  return parsePath(pathname).logicalPath
}

/**
 * Build the real, public URL path for a logical path in the given language.
 *
 * The path is returned WITH a trailing slash (except the English root '/'),
 * because the host (Netlify) serves the prerendered directory-index files at
 * the trailing-slash URL and 301-redirects the non-slash form to it. Emitting
 * the trailing-slash form keeps canonical / sitemap / hreflang / internal
 * links pointing at the 200 URL instead of a redirect.
 */
export function withLangPrefix(logicalPath: string, lang: Lang): string {
  // Normalize to a leading-slash, no-trailing-slash logical path first.
  let logical = logicalPath.startsWith('/') ? logicalPath : '/' + logicalPath
  if (logical.length > 1 && logical.endsWith('/')) logical = logical.slice(0, -1)

  if (lang === 'ko') {
    return logical === '/' ? KO_PREFIX + '/' : KO_PREFIX + logical + '/'
  }
  return logical === '/' ? '/' : logical + '/'
}

/** The other language. */
export function otherLang(lang: Lang): Lang {
  return lang === 'ko' ? 'en' : 'ko'
}
